/**
 * Telemetry staleness watchdog.
 *
 * Doc 007 — Telemetry State Machine:
 *   age < 3s         → live
 *   3s ≤ age < 10s   → stale  (Status badge: "Stale", muted)
 *   age ≥ 10s        → lost   (Status badge: "Lost", crit; downstream
 *                              OI polygon renders stale-gray,
 *                              Telemetry-Lost alert fires)
 *   age ≥ 120s       → evicted (drone removed from the store/map — no
 *                              heartbeat for 2 min. Matches the aircraft
 *                              eviction watchdog, config.aircraft.staleMs.)
 *
 * The drones store tracks `received_at_ms` on every `applyTelemetry`
 * write but never transitions `staleness` once it's set to `'live'`.
 * This component ticks once per second, scans the fleet, calls
 * `markStaleness` for any drone whose age has crossed a threshold, then
 * prunes any drone past the heartbeat deadline.
 *
 * Cheap: a few microseconds per tick even with hundreds of drones (one
 * map iteration + a state comparison per entry). State writes only
 * happen when the bucket actually changes, so a busy fleet doesn't
 * thrash the store.
 */
import { useEffect } from 'react';
import { useDronesStore, type Staleness } from '@/lib/store/drones';

const TICK_MS = 1_000;
/** No telemetry for this long → drop the drone entirely. Matches the
 *  aircraft eviction watchdog (config.aircraft.staleMs = 120_000). */
const HEARTBEAT_MS = 120_000;
export const STALE_MS = 3_000;
export const LOST_MS  = 10_000;

function bucket(ageMs: number): Staleness {
  if (ageMs >= LOST_MS) return 'lost';
  if (ageMs >= STALE_MS) return 'stale';
  return 'live';
}

export function StalenessWatchdog() {
  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = Date.now();
      const fleet = useDronesStore.getState().byId;
      const mark = useDronesStore.getState().markStaleness;
      for (const d of Object.values(fleet)) {
        const next = bucket(now - d.received_at_ms);
        if (next !== d.staleness) mark(d.id, next);
      }
      // Drop drones past the heartbeat deadline. prune() is a no-op (returns
      // the same state ref) when nothing is stale, so this is free on a
      // healthy fleet.
      useDronesStore.getState().prune(HEARTBEAT_MS);
    }, TICK_MS);
    return () => clearInterval(timer);
  }, []);
  return null;
}
